import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Check,
  Phone,
  ShieldCheck,
  Snowflake,
  Timer,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { trackPixel } from "@/lib/metaPixel";
import { getUtm } from "@/lib/utm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["Pièces", "Logement", "Coordonnées"];

const OPTIONS = {
  rooms: ["1 pièce", "2 pièces", "3 pièces", "4 pièces et +"],
  clim_usage: ["Rafraîchir l'été", "Rafraîchir + chauffer (réversible)"],
  property_type: ["Maison", "Appartement"],
  surface_range: ["Moins de 70 m²", "70 à 110 m²", "110 à 150 m²", "Plus de 150 m²"],
};

const SOLUTIONS = {
  "1 pièce": { name: "Monosplit Daikin", price: "dès 2 500 € posé", sub: "1 unité intérieure + 1 groupe extérieur. Pose et mise en service comprises." },
  "2 pièces": { name: "Bisplit Daikin", price: "devis sur mesure", sub: "2 unités intérieures sur 1 seul groupe extérieur. Étude gratuite sur place." },
  "3 pièces": { name: "Multisplit Daikin · 3 unités", price: "devis sur mesure", sub: "3 unités intérieures sur 1 groupe extérieur. Étude gratuite sur place." },
  "4 pièces et +": { name: "Multisplit ou gainable Daikin", price: "devis sur mesure", sub: "Solution maison entière, discrète et homogène. Étude gratuite sur place." },
};

const slug = (v) => v.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase().replace(/^-|-$/g, "");
const inputCls =
  "w-full rounded-xl border border-glacier-border bg-white px-4 py-3 text-ink placeholder-slate-400 outline-none transition-colors focus:border-ice";
const labelCls = "block font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-ice";

const INITIAL = {
  product: "clim",
  rooms: "",
  clim_usage: "",
  property_type: "",
  surface_range: "",
  postal_code: "",
  contact_name: "",
  phone: "",
  email: "",
  consent: false,
  urgent: false,
};

const Option = ({ field, value, data, setData }) => {
  const active = data[field] === value;
  return (
    <button
      type="button"
      data-testid={`clim-option-${field}-${slug(value)}`}
      onClick={() => setData((d) => ({ ...d, [field]: value }))}
      className={`flex min-h-[48px] items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left text-sm font-semibold transition-all ${
        active
          ? "border-ice bg-ice-light text-ink shadow-ice"
          : "border-glacier-border bg-white text-slate-600 hover:border-ice/50 hover:text-ink"
      }`}
    >
      {value}
      {active && <Check className="h-4 w-4 shrink-0 text-ice" />}
    </button>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-3">
    <span className={labelCls}>{label}</span>
    {children}
  </div>
);

export default function ClimSimulator({ variant = "default" }) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const solution = useMemo(() => SOLUTIONS[data.rooms] || null, [data.rooms]);
  const solutionLabel = solution ? `${solution.name} — ${solution.price}` : "";

  const canNext = () => {
    if (step === 1) return data.rooms && data.clim_usage;
    if (step === 2)
      return data.property_type && data.surface_range && /^[0-9]{5}$/.test(data.postal_code);
    return false;
  };

  const canSubmit = () => {
    const digits = data.phone.replace(/\D/g, "");
    const emailOk = !data.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email);
    return (
      data.contact_name.trim().length >= 2 &&
      digits.length >= 10 &&
      digits.length <= 13 &&
      emailOk &&
      data.consent
    );
  };

  const submit = async () => {
    if (!canSubmit()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...data, ...getUtm(), variant, estimated_aids: solutionLabel });
      trackPixel("Lead", {
        content_name: "climatisation-daikin",
        content_category: data.rooms,
        variant,
        urgent: data.urgent,
      });
      setDone(true);
    } catch (e) {
      toast.error("Une erreur est survenue. Réessayez ou appelez-nous.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="devis"
      data-testid="clim-section-devis"
      className="relative overflow-hidden bg-glacier py-24 sm:py-32"
    >
      <div className="pointer-events-none absolute left-1/2 top-0 h-[500px] w-[900px] -translate-x-1/2 rounded-full bg-ice-light blur-[120px]" />
      <div className="relative mx-auto max-w-3xl px-5 sm:px-8">
        <div className="text-center">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-ice sm:text-xs">
            Devis gratuit en 3 étapes
          </p>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-ink sm:text-5xl">
            Votre climatisation Daikin, chiffrée en 1 minute.
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-sm text-slate-500">
            Une solution conseillée immédiatement, puis un appel d'un technicien
            Ener Solutions sous 24 h ouvrées pour affiner le devis.
          </p>
        </div>

        <div className="mt-12 rounded-3xl border border-glacier-border bg-white p-6 shadow-card sm:p-10">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="success"
                data-testid="clim-simulator-success"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="py-8 text-center"
              >
                <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-ice-light text-ice">
                  <Snowflake className="h-7 w-7" />
                </span>
                <h3 className="mt-6 font-display text-2xl font-bold text-ink sm:text-3xl">
                  C'est envoyé, {data.contact_name.split(" ")[0]} !
                </h3>
                <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-slate-600">
                  Solution conseillée : <strong className="text-ice">{solutionLabel}</strong>.{" "}
                  {data.urgent ? (
                    <span data-testid="clim-success-urgent">
                      Demande <strong className="text-ink">prioritaire</strong> transmise :
                      un technicien Ener Solutions vous rappelle{" "}
                      <strong className="text-ink">dans l'heure</strong> (aux horaires
                      d'ouverture) pour planifier l'étude gratuite.
                    </span>
                  ) : (
                    "Un technicien Ener Solutions vous rappelle sous 24 h ouvrées pour planifier l'étude gratuite à domicile."
                  )}
                </p>
                <a
                  href="tel:+33535759803"
                  data-testid="clim-simulator-success-phone"
                  className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-slate-300 px-7 py-3.5 text-sm font-semibold text-ink transition-all hover:border-ice hover:text-ice"
                >
                  <Phone className="h-4 w-4" /> Vous préférez appeler ? 05 35 75 98 03
                </a>
              </motion.div>
            ) : (
              <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="flex items-center justify-between gap-4">
                  <p className="text-xs font-semibold text-slate-500" data-testid="clim-simulator-step-label">
                    Étape {step}/3 — {STEPS[step - 1]}
                  </p>
                  <p className="text-xs font-semibold text-ice">
                    {Math.round(((step - 1) / 3) * 100)}%
                  </p>
                </div>
                <div className="mt-3 flex gap-2">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      data-testid={`clim-simulator-progress-${i}`}
                      className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                        i <= step ? "bg-ice" : "bg-glacier-border"
                      }`}
                    />
                  ))}
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.3 }}
                    className="mt-8 space-y-7"
                  >
                    {step === 1 && (
                      <>
                        <Field label="Combien de pièces à climatiser ?">
                          <div className="grid grid-cols-2 gap-3">
                            {OPTIONS.rooms.map((v) => (
                              <Option key={v} field="rooms" value={v} data={data} setData={setData} />
                            ))}
                          </div>
                        </Field>
                        <Field label="Votre besoin">
                          <div className="grid gap-3 sm:grid-cols-2">
                            {OPTIONS.clim_usage.map((v) => (
                              <Option key={v} field="clim_usage" value={v} data={data} setData={setData} />
                            ))}
                          </div>
                        </Field>
                      </>
                    )}

                    {step === 2 && (
                      <>
                        <Field label="Votre logement">
                          <div className="grid grid-cols-2 gap-3">
                            {OPTIONS.property_type.map((v) => (
                              <Option key={v} field="property_type" value={v} data={data} setData={setData} />
                            ))}
                          </div>
                        </Field>
                        <Field label="Surface habitable">
                          <div className="grid grid-cols-2 gap-3">
                            {OPTIONS.surface_range.map((v) => (
                              <Option key={v} field="surface_range" value={v} data={data} setData={setData} />
                            ))}
                          </div>
                        </Field>
                        <Field label="Code postal">
                          <input
                            data-testid="clim-input-postal"
                            inputMode="numeric"
                            maxLength={5}
                            placeholder="ex. 33000"
                            value={data.postal_code}
                            onChange={(e) =>
                              setData((d) => ({ ...d, postal_code: e.target.value.replace(/\D/g, "") }))
                            }
                            className={inputCls}
                          />
                        </Field>
                      </>
                    )}

                    {step === 3 && (
                      <>
                        <div
                          data-testid="clim-simulator-result"
                          className="rounded-2xl border border-ice/25 bg-ice-light/60 p-6"
                        >
                          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-ice">
                            Solution conseillée
                          </p>
                          <p className="mt-3 font-display text-2xl font-extrabold text-ink sm:text-4xl">
                            {solution?.name}
                          </p>
                          <p className="mt-1 font-display text-xl font-bold text-ice sm:text-2xl" data-testid="clim-simulator-price">
                            {solution?.price}
                          </p>
                          <p className="mt-3 text-sm text-slate-600">{solution?.sub}</p>
                          <p className="mt-3 flex items-center gap-2 text-xs font-semibold text-forest">
                            <Timer className="h-3.5 w-3.5" /> Installation sous 15 jours · Zéro sous-traitance
                          </p>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                          <Field label="Prénom & nom">
                            <input
                              data-testid="clim-input-name"
                              placeholder="ex. Marie Dupont"
                              value={data.contact_name}
                              onChange={(e) => setData((d) => ({ ...d, contact_name: e.target.value }))}
                              className={inputCls}
                            />
                          </Field>
                          <Field label="Téléphone mobile">
                            <input
                              data-testid="clim-input-phone"
                              type="tel"
                              placeholder="ex. 06 12 34 56 78"
                              value={data.phone}
                              onChange={(e) => setData((d) => ({ ...d, phone: e.target.value }))}
                              className={inputCls}
                            />
                          </Field>
                        </div>
                        <Field label="Email (facultatif)">
                          <input
                            data-testid="clim-input-email"
                            type="email"
                            placeholder="ex. marie@email.fr"
                            value={data.email}
                            onChange={(e) => setData((d) => ({ ...d, email: e.target.value }))}
                            className={inputCls}
                          />
                        </Field>
                        <button
                          type="button"
                          data-testid="clim-urgent-toggle"
                          aria-pressed={data.urgent}
                          onClick={() => setData((d) => ({ ...d, urgent: !d.urgent }))}
                          className={`flex min-h-[56px] w-full items-center gap-4 rounded-2xl border px-4 py-3.5 text-left transition-all ${
                            data.urgent
                              ? "border-amber-400 bg-amber-50 shadow-[0_0_24px_rgba(251,191,36,0.25)]"
                              : "border-glacier-border bg-glacier hover:border-amber-300"
                          }`}
                        >
                          <span
                            className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl transition-colors ${
                              data.urgent ? "bg-amber-400 text-ink" : "bg-white text-amber-500"
                            }`}
                          >
                            <Zap className="h-5 w-5" />
                          </span>
                          <span className="flex-1">
                            <span className="block font-display text-sm font-bold text-ink sm:text-base">
                              Être rappelé dans l'heure
                            </span>
                            <span className="block text-xs text-slate-500">
                              Votre demande passe en priorité : un technicien vous appelle dans
                              l'heure aux horaires d'ouverture.
                            </span>
                          </span>
                          <span
                            className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                              data.urgent ? "bg-amber-400" : "bg-slate-300"
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                                data.urgent ? "translate-x-[22px]" : "translate-x-0.5"
                              }`}
                            />
                          </span>
                        </button>
                        <label className="flex cursor-pointer items-start gap-3">
                          <input
                            type="checkbox"
                            data-testid="clim-consent"
                            checked={data.consent}
                            onChange={(e) => setData((d) => ({ ...d, consent: e.target.checked }))}
                            className="mt-0.5 h-4 w-4 accent-sky-600"
                          />
                          <span className="text-sm leading-relaxed text-slate-600">
                            J'accepte d'être recontacté(e) par Ener Solutions au
                            sujet de mon projet de climatisation.
                          </span>
                        </label>
                        <p className="flex items-center gap-2 text-xs text-slate-400">
                          <ShieldCheck className="h-4 w-4 text-ice/70" />
                          Vos données restent confidentielles et ne sont jamais revendues.
                        </p>
                      </>
                    )}
                  </motion.div>
                </AnimatePresence>

                <div className="mt-10 flex items-center justify-between gap-4">
                  {step > 1 ? (
                    <button
                      data-testid="clim-simulator-back"
                      onClick={() => setStep((s) => s - 1)}
                      className="flex min-h-[48px] items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-600 transition-all hover:border-ice hover:text-ice"
                    >
                      <ArrowLeft className="h-4 w-4" /> Retour
                    </button>
                  ) : (
                    <span />
                  )}
                  {step < 3 ? (
                    <button
                      data-testid="clim-simulator-next"
                      disabled={!canNext()}
                      onClick={() => setStep((s) => s + 1)}
                      className={`group flex min-h-[48px] items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold transition-all ${
                        canNext()
                          ? "bg-ice text-white hover:scale-[1.03] hover:bg-ice-hover"
                          : "cursor-not-allowed bg-slate-100 text-slate-400"
                      }`}
                    >
                      Continuer
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </button>
                  ) : (
                    <button
                      data-testid="clim-simulator-submit"
                      disabled={!canSubmit() || submitting}
                      onClick={submit}
                      className={`flex min-h-[48px] items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold transition-all ${
                        canSubmit() && !submitting
                          ? data.urgent
                            ? "bg-amber-400 text-ink hover:scale-[1.03] hover:bg-amber-300"
                            : "bg-ice text-white hover:scale-[1.03] hover:bg-ice-hover hover:shadow-ice"
                          : "cursor-not-allowed bg-slate-100 text-slate-400"
                      }`}
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Envoi…
                        </>
                      ) : (
                        <>
                          {data.urgent ? "Être rappelé dans l'heure" : "Recevoir mon devis gratuit"}{" "}
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
